/**
 * Плавающая кнопка «Запустить» нового монтажа. Стоит в правом нижнем углу над
 * вкладками и показывает, готовы ли материалы. В конце страницы раскрывается на
 * всю ширину, у готовых материалов — с условиями списания: тогда тап запускает
 * монтаж. Тап по зелёной выше по странице сначала уводит в конец.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import * as telegram from '../lib/telegram';
import { t } from '../i18n';

/** empty — материалов нет, uploading — грузятся, check — есть ошибки загрузки, ready — можно запускать. */
export type LaunchStage = 'empty' | 'uploading' | 'check' | 'ready';

const reducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const scrollBottom = (): number => document.documentElement.scrollHeight - window.innerHeight;

/**
 * Страница докручена до конца. Раскрытая кнопка выше свёрнутой и удлиняет
 * страницу, поэтому раскрываем у самого низа, а сворачиваем с запасом —
 * иначе кнопка мигала бы на границе.
 */
function useAtBottom(): boolean {
  const [atBottom, setAtBottom] = useState(false);
  useEffect(() => {
    let frame = 0;
    const update = (): void => {
      frame = 0;
      const left = scrollBottom() - window.scrollY;
      setAtBottom(current => current ? left <= 120 : left <= 24);
    };
    const schedule = (): void => { if (!frame) frame = requestAnimationFrame(update); };
    // Высота страницы меняется и без прокрутки: раскрыли материалы, догрузились картинки.
    const observer = new ResizeObserver(schedule);
    observer.observe(document.body);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    update();
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      cancelAnimationFrame(frame);
    };
  }, []);
  return atBottom;
}

export function LaunchButton({ hidden, retry, stage, percent, loading, caption, onShowMaterials, onLaunch }: {
  hidden: boolean;
  /** Прошлый монтаж не удался: кнопка повторяет его. */
  retry: boolean;
  stage: LaunchStage;
  percent: number;
  loading: boolean;
  /** Две строки под «Запустить монтаж»: водяной знак и сколько спишется. */
  caption: [string, string | null];
  onShowMaterials: () => void;
  onLaunch: () => void;
}): ReactElement | null {
  const atBottom = useAtBottom();
  const ready = stage === 'ready';
  const confirming = atBottom && ready;
  const rowRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  const title = loading ? (ready ? t('components.launch.starting') : t('components.launch.wait')) : stage === 'uploading' ? t('components.launch.uploading', { percent })
    : confirming ? (retry ? t('components.launch.retryEdit') : t('components.launch.startEdit')) : retry ? t('components.launch.retry') : t('components.launch.start');

  // Ширину auto браузер не анимирует: задаём max-width в пикселях — по тексту
  // свёрнутой кнопки или по всей строке, а переход между ними делает CSS.
  useLayoutEffect(() => {
    const row = rowRef.current;
    const button = row?.firstElementChild as HTMLElement | null;
    if (!row || !button) return;
    const fit = (): void => {
      const width = atBottom ? row.clientWidth : (titleRef.current?.offsetWidth ?? 0) + 40;
      button.style.maxWidth = `${Math.min(width, row.clientWidth)}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [atBottom, title, hidden]);

  if (hidden) return null;

  const press = (): void => {
    if (loading || stage === 'uploading') return;
    if (!ready) { onShowMaterials(); return; }
    telegram.hapticFeedback.impact('light');
    // Сначала показываем условия списания в конце страницы, запуск — следующим тапом.
    if (!confirming) {
      window.scrollTo({ top: scrollBottom(), behavior: reducedMotion() ? 'instant' : 'smooth' });
      return;
    }
    onLaunch();
  };

  return (
    <div ref={rowRef} className={'launch' + (atBottom ? ' expanded' : '')}>
      <button type="button" className={'launch-btn' + (ready ? ' ready' : '')} aria-busy={loading || stage === 'uploading'} onClick={press}>
        <span ref={titleRef} className="launch-title">{title}</span>
        {ready ? <span className={'launch-captions' + (confirming ? ' open' : '')} aria-hidden={!confirming}>
          {caption.filter((line): line is string => !!line).map(line => <span key={line} className="launch-caption">{line}</span>)}
        </span> : null}
      </button>
    </div>
  );
}
